import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { useContent } from "../../lib/contentContext";

export default function Founder() {
    const { content } = useContent();
    const F = content.founder;
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [80, -80]);
    const yBadge = useTransform(scrollYProgress, [0, 1], [40, -40]);

    return (
        <section
            id="founder"
            ref={ref}
            className="relative overflow-hidden bg-cream py-20 sm:py-24 lg:py-40"
            data-testid="founder-section"
        >
            <div className="mx-auto max-w-[1600px] px-4 sm:px-8">
                <div className="grid grid-cols-1 items-start gap-16 lg:grid-cols-[1fr_1.1fr] lg:gap-24">
                    <div className="relative">
                        <div className="relative aspect-[4/5] overflow-hidden">
                            <div className="absolute inset-0 z-10 border border-ink/20" />
                            <motion.img
                                style={{ y }}
                                src={F.portrait_url}
                                alt={F.name}
                                className="absolute inset-0 h-[115%] w-full object-cover object-top"
                            />
                            <div className="absolute inset-0 z-[11] bg-gradient-to-t from-cream/60 via-transparent to-transparent" />
                        </div>
                        {/* Founder credential card — in normal flow, sits under the portrait, offsets slightly upward on desktop for editorial layering */}
                        <motion.div
                            style={{ y: yBadge }}
                            className="relative z-20 -mt-6 ml-auto w-fit max-w-[85%] border border-gold bg-cream px-5 py-4 shadow-[0_20px_40px_-20px_rgba(0,0,0,0.35)] sm:px-6 sm:py-5 lg:mr-[-2.5rem]"
                        >
                            <div className="label text-ink/50">Founder</div>
                            <div className="font-serif text-2xl italic text-ink sm:text-3xl">
                                {F.name}
                            </div>
                            <div className="label text-gold">{F.title}</div>
                        </motion.div>
                        <div className="mt-8 label leading-relaxed text-ink/50 sm:mt-10">
                            {F.subtitle}
                        </div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: [0.2, 0.8, 0.2, 1] }}
                        viewport={{ once: true, margin: "-120px" }}
                    >
                        <div className="label mb-6 flex items-center gap-3 text-gold-dark">
                            <span className="h-px w-10 bg-gold-dark/60" />
                            02 — The Founder
                        </div>
                        <h2 className="font-serif text-3xl leading-[0.98] tracking-tight text-ink sm:text-5xl lg:text-6xl">
                            {F.heading_line1} <br />
                            {F.heading_line2}{" "}
                            <em className="italic text-gold-dark">
                                {F.heading_italic}
                            </em>
                        </h2>
                        <div className="mt-10 space-y-5 text-base leading-relaxed text-ink/80 sm:text-lg">
                            {(F.bio || []).map((p, i) => (
                                <p key={i}>{p}</p>
                            ))}
                        </div>

                        <div className="mt-12 grid grid-cols-2 gap-6 border-y border-ink/10 py-8 sm:grid-cols-3">
                            {[
                                { k: "Complimentary", v: "Consultations" },
                                { k: "Evidence-", v: "informed care" },
                                { k: "1-on-1", v: "with the founder" },
                            ].map((s, i) => (
                                <div key={i}>
                                    <div className="label text-gold-dark">
                                        {s.k}
                                    </div>
                                    <div className="mt-2 font-serif text-xl italic text-ink sm:text-2xl">
                                        {s.v}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-10 flex flex-wrap items-center gap-6">
                            <Link
                                to="/book"
                                data-testid="founder-cta-book"
                                className="group relative inline-flex items-center gap-4 border border-ink bg-ink px-6 py-3.5 text-[11px] tracking-[0.24em] uppercase font-medium text-cream transition-transform duration-500 hover:translate-y-[-2px] sm:px-7 sm:py-4 sm:text-[12px]"
                            >
                                Meet with Crystal
                                <span className="h-px w-8 bg-gold transition-all duration-500 group-hover:w-12" />
                            </Link>
                            <div className="font-serif text-xl italic text-ink/70 sm:text-2xl">
                                — Your journey starts with a conversation.
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
